/**
 * SFX — procedural sound effects synthesized via Web Audio API.
 * No assets needed. Plays through the Phaser sound context so it
 * shares the same gain bus as music + voice.
 *
 * Each SFX is a tiny tone shaped by an envelope. Together they form
 * a coherent sonic palette: shimmery, chime-like, contemplative.
 */
class SFXClass {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.volume = 0.35;
    this._enabled = true;
  }

  /** Lazy-init the audio context. Called by AudioManager once unlocked. */
  init(ctx) {
    if (this.ctx) return;
    this.ctx = ctx;
    this.master = ctx.createGain();
    this.master.gain.value = this.volume;
    this.master.connect(ctx.destination);
  }

  setEnabled(flag) { this._enabled = flag; if (this.master) this.master.gain.value = flag ? this.volume : 0; }

  // ── Helpers ──────────────────────────────────────────────────────

  _envGain(start, peak, sustain, release) {
    if (!this.ctx) return null;
    const g = this.ctx.createGain();
    const t = this.ctx.currentTime;
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(peak, t + start);
    g.gain.linearRampToValueAtTime(sustain, t + start + 0.05);
    g.gain.exponentialRampToValueAtTime(0.0001, t + start + release);
    return g;
  }

  _osc(freq, type = 'sine') {
    const o = this.ctx.createOscillator();
    o.type = type;
    o.frequency.value = freq;
    return o;
  }

  // ── SFX library ──────────────────────────────────────────────────

  /** Plant tend — soft chime */
  tend() {
    if (!this._enabled || !this.ctx) return;
    const t = this.ctx.currentTime;
    const notes = [880, 1108, 1318]; // A5, C#6, E6 — major triad
    notes.forEach((f, i) => {
      const o = this._osc(f, 'sine');
      const g = this._envGain(0.005 + i * 0.04, 0.25, 0.15, 0.6);
      o.connect(g).connect(this.master);
      o.start(t);
      o.stop(t + 0.7);
    });
  }

  /** Petal burst — soft white-noise sweep + bell */
  burst() {
    if (!this._enabled || !this.ctx) return;
    const t = this.ctx.currentTime;
    // Noise via random buffer
    const noiseBuf = this.ctx.createBuffer(1, this.ctx.sampleRate * 0.4, this.ctx.sampleRate);
    const data = noiseBuf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * 0.5;
    const noise = this.ctx.createBufferSource();
    noise.buffer = noiseBuf;
    const filt = this.ctx.createBiquadFilter();
    filt.type = 'bandpass';
    filt.frequency.setValueAtTime(2000, t);
    filt.frequency.exponentialRampToValueAtTime(400, t + 0.4);
    filt.Q.value = 4;
    const ng = this.ctx.createGain();
    ng.gain.setValueAtTime(0, t);
    ng.gain.linearRampToValueAtTime(0.35, t + 0.02);
    ng.gain.exponentialRampToValueAtTime(0.0001, t + 0.4);
    noise.connect(filt).connect(ng).connect(this.master);
    noise.start(t);
    noise.stop(t + 0.45);

    // Bell layer
    const bellNotes = [1318, 1760, 2349]; // E6, A6, D7
    bellNotes.forEach((f, i) => {
      const o = this._osc(f, 'triangle');
      const g = this._envGain(0.01 + i * 0.02, 0.18, 0.1, 0.8);
      o.connect(g).connect(this.master);
      o.start(t);
      o.stop(t + 0.9);
    });
  }

  /** Lightning strike — crackle + low boom */
  lightning() {
    if (!this._enabled || !this.ctx) return;
    const t = this.ctx.currentTime;
    // Noise crackle
    const noiseBuf = this.ctx.createBuffer(1, this.ctx.sampleRate * 0.18, this.ctx.sampleRate);
    const data = noiseBuf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * 0.7;
    const noise = this.ctx.createBufferSource();
    noise.buffer = noiseBuf;
    const filt = this.ctx.createBiquadFilter();
    filt.type = 'highpass';
    filt.frequency.value = 4000;
    const ng = this.ctx.createGain();
    ng.gain.setValueAtTime(0.5, t);
    ng.gain.exponentialRampToValueAtTime(0.0001, t + 0.18);
    noise.connect(filt).connect(ng).connect(this.master);
    noise.start(t);
    noise.stop(t + 0.2);

    // Low rumble
    const o = this._osc(60, 'sine');
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.5, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.5);
    o.connect(g).connect(this.master);
    o.start(t);
    o.stop(t + 0.55);
  }

  /** Memory unlock — long shimmery sweep */
  memoryUnlock() {
    if (!this._enabled || !this.ctx) return;
    const t = this.ctx.currentTime;
    // Ascending arpeggio
    const notes = [523, 659, 784, 988, 1175, 1568]; // C5 → G6
    notes.forEach((f, i) => {
      const o = this._osc(f, 'sine');
      const g = this._envGain(i * 0.08, 0.2, 0.12, 1.2);
      o.connect(g).connect(this.master);
      o.start(t);
      o.stop(t + 1.4 + i * 0.08);
    });
  }

  /** Soft pop — lightweight tap acknowledgement */
  tap() {
    if (!this._enabled || !this.ctx) return;
    const t = this.ctx.currentTime;
    const o = this._osc(1200, 'sine');
    const g = this._envGain(0.005, 0.15, 0.05, 0.15);
    o.connect(g).connect(this.master);
    o.start(t);
    o.stop(t + 0.2);
  }
}

export const SFX = new SFXClass();
